import nth from 'lodash/nth'
import { useEffect, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid'

import { DeliveryMethod, useGetWorkOrderTasksQuery, useManualOrderQuery } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import Money from '@/gf/modules/Money'
import useForm from './ManualOrder/useForm'

import Box from '@/buyers/components/Box'
import DocumentUpload from '@/buyers/components/DocumentUpload'
import Frame from '@/buyers/components/Frame'
import Content from '@/buyers/components/LimitedFrame/Content'
import SidebarLayout from '@/buyers/components/SidebarLayout'
import Action from '@/gf/components/Action'
import AddressInput from '@/gf/components/AddressInput'
import Field from '@/gf/components/Field'
import Form from '@/gf/components/Form'
import DeliveryMethodInput from '@/gf/components/inputs/DeliveryMethod'
import TextInput from '@/gf/components/inputs/Text'
import Link from '@/gf/components/Link'
import PartsForm from './RFQ/PartsForm'
import PricesForm from './RFQ/PricesForm'
import VendorsSelector from './CreateExternalOrder/VendorsSelector'
import useUpload from './CreateExternalOrder/useUpload'

const zeroPrice = Money.fromDecimal(0, 'USD')

const ManualOrder = () => {
  const { orgId, featureFlags } = useSession()
  const { rfqId } = useParams() as { rfqId: string }
  const client = useGqlClient()
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  // The uploaded object's own URL is what gets extracted and submitted; only the preview
  // reads through the authed proxy (GEA-4585). Null with the proxy off — render the object.
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState<string | null>(null)

  const {
    fields,
    errors,
    submitting,
    buildPart,
    addPart,
    removePart,
    updatePart,
    updateFields,
    submit,
  } = useForm({ rfqId })

  const { uploadProcessing, uploadStarted, resetUpload, extractOrderDoc } = useUpload(
    (orderDoc, documentId) => {
      const parts = orderDoc.items!.map(({ properties }) => {
        const unitPrice = properties.find((p) => p.__typename === 'OrderDocItemUnitPrice')?.price
        const totalPrice = properties.find((p) => p.__typename === 'OrderDocItemTotalPrice')?.price
        const quantity = properties.find((p) => p.__typename === 'OrderDocItemQuantity')?.quantity

        return buildPart({
          mpn: properties.find((p) => p.__typename === 'OrderDocItemPartNumber')?.text || '',
          name: properties.find((p) => p.__typename === 'OrderDocItemDescription')?.text || '',
          unitPrice: unitPrice || totalPrice || null,
          quantity: (unitPrice && quantity) || 1,
        })
      })

      updateFields({
        parts: parts.length > 0 ? parts : [buildPart()],
        taxCost: orderDoc.properties.find((p) => p.__typename === 'OrderDocTax')?.price || null,
        shippingCost:
          orderDoc.properties.find((p) => p.__typename === 'OrderDocShippingCost')?.price || null,
        documentId,
      })
    }
  )

  const onDocumentUploadComplete = (url: string, previewUrl: string | null) => {
    setDocumentUrl(url)
    setDocumentPreviewUrl(previewUrl)
    extractOrderDoc(url)
  }

  const clearDocument = () => {
    setDocumentUrl(null)
    setDocumentPreviewUrl(null)
    resetUpload()
  }

  const { data, previousData } = useManualOrderQuery({
    variables: {
      orgId,
      rfqFilter: JSON.stringify(['id_eq', rfqId]),
      vendorFilter: JSON.stringify(['id_eq', fields.vendorId]),
    },
    client,
  })

  const { org } = data || previousData || {}
  const vendor = org?.vendors.find((v) => v.id === fields.vendorId)
  const rfq = nth((data || previousData)?.rfqs, 0)

  // GEA-11818: a part added here is born on this form, so it gets the job-code picker by the
  // same rule as create (PartDetailsStep) and the request page's Add part. The picker shows
  // when the request has an M5 work order and the auto-open is on. The code is required only
  // when the work order has job lines (a WO with none is the GEA-5927 case).
  const jobCodeWorkOrderId =
    (!!featureFlags.assetworksApiIssuePartsToWorkOrder &&
      !!featureFlags.assetworksAutoOpenPartRequests &&
      rfq?.workOrderNumber) ||
    ''

  const { data: workOrderTasksData, loading: workOrderTasksLoading } = useGetWorkOrderTasksQuery({
    client,
    variables: { workOrderId: jobCodeWorkOrderId },
    skip: !jobCodeWorkOrderId,
  })

  const workOrderTasks = workOrderTasksData?.getWorkOrderTasks ?? []
  const jobCodeRequired = !!jobCodeWorkOrderId && workOrderTasks.length > 0
  // Until the job lines arrive, the form cannot know whether a code is required. Hold the submit,
  // or a line sent in that window opens under the work order's first open job.
  const jobCodesLoading = !!jobCodeWorkOrderId && workOrderTasksLoading
  // Start each added line on the first job line, the same one the M5 open picks today. It
  // stays editable.
  const defaultJobCode = jobCodeWorkOrderId ? workOrderTasks[0]?.taskId : undefined
  const addedLineNeedsDefault = !!fields.parts?.some((p) => !p.rfqPartId && !p.taskNumber)

  useEffect(() => {
    if (!defaultJobCode || !addedLineNeedsDefault || !fields.parts) return

    updateFields({
      parts: fields.parts.map((p) =>
        !p.rfqPartId && !p.taskNumber ? { ...p, taskNumber: defaultJobCode } : p
      ),
    })
  }, [defaultJobCode, addedLineNeedsDefault, fields.parts])

  useEffect(() => {
    if (rfq && !fields.parts) {
      const parts =
        rfq.parts.length > 0
          ? rfq.parts.map((p) =>
              buildPart({
                mpn: p.mpn,
                rfqPartMpn: p.mpn,
                rfqPartId: p.id,
                name: p.description,
                quantity: p.quantity,
                unitPrice: zeroPrice,
              })
            )
          : [buildPart()]

      updateFields({ parts })
    }
  }, [!rfq])

  useEffect(() => {
    if (vendor)
      updateFields({
        pickupAddress: vendor.address ||
          vendor?.store?.address || {
            firstName: '',
            lastName: '',
            companyName: '',
            lineOne: '',
            lineTwo: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'United States',
          },
      })
  }, [vendor?.id])

  if (!rfq || !org || !fields.parts) return null

  const breadcrumbs = {
    copy: 'Back to Dashboard',
    crumbs: [
      { name: 'Requests', href: '/rfqs' },
      { name: `Request ${rfq.shortId}`, href: `/rfqs/${rfqId}` },
      { name: 'Order Manually', href: `/rfqs/${rfqId}/order-manually` },
    ],
  }

  const subtotal = fields.parts
    .map((p) => Money.mult(p.unitPrice || zeroPrice, p.quantity || 1))
    .reduce(Money.add)

  const hasIncompleteShippingAddress =
    rfq.shippingAddress &&
    (!rfq.shippingAddress.lineOne || !rfq.shippingAddress.city || !rfq.shippingAddress.postalCode)

  return (
    <Frame breadcrumbs={breadcrumbs} contentClassName="flex flex-col">
      <Content className="flex flex-col grow py-4 space-y-4">
        <div className="text-xl">Order Manually</div>

        {hasIncompleteShippingAddress && rfq.shippingLocation && (
          <RouterLink
            to={`/locations/${rfq.shippingLocation.id}?edit=true`}
            className="inline-flex items-center px-3 py-1.5 rounded-md bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm font-medium hover:bg-yellow-100 transition-colors w-fit"
          >
            <ExclamationTriangleIcon className="w-4 h-4 mr-1.5 text-yellow-600" />
            Incomplete address - click to update
          </RouterLink>
        )}

        <Form
          onSubmit={() => {
            if (!jobCodesLoading) submit({ jobCodeRequired })
          }}
        >
          <SidebarLayout>
            <Box className="space-y-4">
              <Field label="Vendor" errors={errors.vendorId}>
                <VendorsSelector
                  value={fields.vendorId}
                  onChange={(vendorId) => updateFields({ vendorId })}
                />
              </Field>

              <PartsForm
                parts={fields.parts}
                errors={errors.parts}
                updatePart={updatePart}
                removePart={removePart}
                addPart={addPart}
                jobCodeWorkOrderId={jobCodeWorkOrderId || undefined}
              />

              <Field label="Delivery Method">
                <DeliveryMethodInput
                  value={fields.deliveryMethod}
                  onChange={(deliveryMethod) => updateFields({ deliveryMethod })}
                  willCallDescription={
                    <>Order will be picked up from the vendor&apos;s location.</>
                  }
                  shipDescription={<>Vendor will ship the order to your location.</>}
                />
              </Field>

              {fields.deliveryMethod === DeliveryMethod.Pickup && (
                <div className="space-y-2">
                  <h2 className="text-base font-medium">Will Call Address</h2>

                  <AddressInput
                    address={fields.pickupAddress}
                    onChange={(pickupAddress) => updateFields({ pickupAddress })}
                    hideFirstLastName
                    hideCompany
                  />
                </div>
              )}
            </Box>

            <div className="space-y-4">
              <Box className="space-y-4">
                <DocumentUpload
                  title="Upload Order"
                  uploadContext="buyer_create_external_order"
                  processing={!!uploadProcessing}
                  processingDescription="This may take up to a minute. Order details will be provided from the information in this file."
                  documentUrl={documentUrl}
                  documentPreviewUrl={documentPreviewUrl}
                  onStarted={uploadStarted}
                  onComplete={onDocumentUploadComplete}
                  onClear={clearDocument}
                />
              </Box>

              <Box className="space-y-4">
                <Field label={`Purchase Order${!org.showPurchaseOrder ? ' (optional)' : ''}`}>
                  {org.generatePurchaseOrderNumber ? (
                    <div className="flex gap-4 items-center text-sm">
                      <span className="text-gray-500 italic">(auto-generated)</span>
                      <Link.T to="/settings/organization#approval-settings">Edit</Link.T>
                    </div>
                  ) : (
                    <TextInput
                      value={fields.purchaseOrder}
                      setValue={(purchaseOrder) => updateFields({ purchaseOrder })}
                      required={org.showPurchaseOrder}
                    />
                  )}
                </Field>

                <Field label="Quote Number (optional)">
                  <TextInput
                    value={fields.quoteNumber}
                    setValue={(quoteNumber) => updateFields({ quoteNumber })}
                  />
                </Field>

                <PricesForm
                  subtotal={subtotal}
                  shipping={fields.shippingCost}
                  tax={fields.taxCost}
                  pickup={false}
                  setTax={(taxCost) => updateFields({ taxCost })}
                  setShipping={(shippingCost) => updateFields({ shippingCost })}
                />

                <div className="flex justify-end">
                  <Action.P type="submit" performing={submitting} disabled={jobCodesLoading}>
                    Create Order
                  </Action.P>
                </div>
              </Box>
            </div>
          </SidebarLayout>
        </Form>
      </Content>
    </Frame>
  )
}

export default ManualOrder
